import {
  CmsContentHomeHeroSchema,
  type CmsContentHomeHeroForm,
} from "@/modules/cms/domain/contracts";
import { uploadImageToKey } from "@/shared/lib/upload-image-to-key";
import { ImageKeyUploadField } from "@/shared/ui/ImageKeyUploadField";
import {
  Button,
  FormCheckbox,
  FormNumber,
  FormRoot,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { PlusIcon, TrashIcon } from "lucide-react";
import { useCallback, useState } from "react";
import type { Resolver, UseFormReturn } from "react-hook-form";
import { z } from "zod";

const HOME_HERO_IMAGE_KEY_PREFIX = "uploads/cms/home-hero";

function fieldKey(
  index: number,
  field: "mobileImageKey" | "tabletImageKey" | "desktopImageKey",
) {
  return `${index}.${field}`;
}

const FormSchema = z.object({
  items: CmsContentHomeHeroSchema,
});
type FormValues = z.infer<typeof FormSchema>;

type HomeHeroFormProps = {
  content: CmsContentHomeHeroForm | null;
  onSubmit: (content: CmsContentHomeHeroForm) => void;
  onCancel: () => void;
  submitting?: boolean;
};

export function HomeHeroForm({
  content,
  onSubmit,
  onCancel,
  submitting,
}: HomeHeroFormProps) {
  const defaultValues: FormValues = {
    items:
      Array.isArray(content) && content.length > 0
        ? content.map((item) => ({ ...item, isActive: item.isActive ?? true }))
        : [],
  };

  const methods = RHF.useForm<FormValues>({
    resolver: zodResolver(FormSchema) as Resolver<FormValues>,
    defaultValues,
  });

  const { fields, append, remove } = RHF.useFieldArray({
    control: methods.control,
    name: "items",
  });

  const watchItems = methods.watch("items") ?? [];
  const setValue = methods.setValue;
  const [pendingFiles, setPendingFiles] = useState<Record<string, File>>({});
  const [uploading, setUploading] = useState(false);

  const setPendingFile = useCallback(
    (
      index: number,
      field: "mobileImageKey" | "tabletImageKey" | "desktopImageKey",
      file: File | null,
    ) => {
      const key = fieldKey(index, field);
      setPendingFiles((prev) => {
        const next = { ...prev };
        if (file) next[key] = file;
        else delete next[key];
        return next;
      });
    },
    [],
  );

  const clearPendingAndKey = useCallback(
    (
      index: number,
      field: "mobileImageKey" | "tabletImageKey" | "desktopImageKey",
    ) => {
      setValue(`items.${index}.${field}`, "", { shouldDirty: true });
      setPendingFiles((prev) => {
        const next = { ...prev };
        delete next[fieldKey(index, field)];
        return next;
      });
    },
    [setValue],
  );

  const handleSubmit = async (vals: FormValues) => {
    const items = vals.items.map((item) => ({
      ...item,
      isActive: item.isActive ?? true,
    }));
    const toUpload = Object.keys(pendingFiles);
    if (toUpload.length > 0) {
      setUploading(true);
      try {
        for (const key of toUpload) {
          const parts = key.split(".");
          const indexStr = parts[0];
          const field = parts[1];
          if (indexStr === undefined || field === undefined) continue;
          const index = Number.parseInt(indexStr, 10);
          const file = pendingFiles[key];
          const item = items[index];
          if (!file || !item) continue;
          const uploadedKey = await uploadImageToKey(
            HOME_HERO_IMAGE_KEY_PREFIX,
            file,
          );
          if (field === "mobileImageKey") item.mobileImageKey = uploadedKey;
          else if (field === "tabletImageKey")
            item.tabletImageKey = uploadedKey;
          else if (field === "desktopImageKey")
            item.desktopImageKey = uploadedKey;
        }
      } finally {
        setUploading(false);
      }
    }
    onSubmit(items);
  };

  return (
    <FormRoot<FormValues>
      methods={methods as UseFormReturn<FormValues>}
      onSubmit={handleSubmit}
    >
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-medium">ລາຍການ Hero</h3>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              append({
                mobileImageKey: "",
                tabletImageKey: "",
                desktopImageKey: "",
                order: fields.length,
                isActive: true,
              })
            }
          >
            <PlusIcon className="h-4 w-4" />
            ເພີ່ມ
          </Button>
        </div>
        {fields.length === 0 && (
          <p className="text-muted-foreground text-sm">
            ຍັງບໍ່ມີລາຍການ. ກົດ ເພີ່ມ ເພື່ອເພີ່ມ slide.
          </p>
        )}
        <div className="space-y-4">
          {fields.map((field, index) => (
            <div
              key={field.id}
              className="space-y-3 rounded-lg border bg-muted/30 p-4"
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-sm">Slide {index + 1}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => remove(index)}
                  aria-label="ລຶບ"
                >
                  <TrashIcon className="h-4 w-4" />
                </Button>
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <ImageKeyUploadField
                  keyPrefix={HOME_HERO_IMAGE_KEY_PREFIX}
                  label="Mobile (4:5)"
                  aspectHint="ອັດສ່ວນ 4:5"
                  aspectRatio="aspect-[4/5]"
                  widthPx={800}
                  heightPx={1000}
                  value={watchItems[index]?.mobileImageKey ?? ""}
                  onChange={(key) =>
                    setValue(`items.${index}.mobileImageKey`, key, {
                      shouldDirty: true,
                    })
                  }
                  disabled={submitting}
                  deferUpload
                  pendingFile={
                    pendingFiles[fieldKey(index, "mobileImageKey")] ?? null
                  }
                  onFileSelect={(file) =>
                    setPendingFile(index, "mobileImageKey", file)
                  }
                  onClear={() => clearPendingAndKey(index, "mobileImageKey")}
                />
                <ImageKeyUploadField
                  keyPrefix={HOME_HERO_IMAGE_KEY_PREFIX}
                  label="Tablet (16:9)"
                  aspectHint="ອັດສ່ວນ 16:9"
                  aspectRatio="aspect-video"
                  widthPx={1280}
                  heightPx={720}
                  value={watchItems[index]?.tabletImageKey ?? ""}
                  onChange={(key) =>
                    setValue(`items.${index}.tabletImageKey`, key, {
                      shouldDirty: true,
                    })
                  }
                  disabled={submitting}
                  deferUpload
                  pendingFile={
                    pendingFiles[fieldKey(index, "tabletImageKey")] ?? null
                  }
                  onFileSelect={(file) =>
                    setPendingFile(index, "tabletImageKey", file)
                  }
                  onClear={() => clearPendingAndKey(index, "tabletImageKey")}
                />
                <ImageKeyUploadField
                  keyPrefix={HOME_HERO_IMAGE_KEY_PREFIX}
                  label="Desktop (8:3)"
                  aspectHint="ອັດສ່ວນ 8:3"
                  aspectRatio="aspect-[8/3]"
                  widthPx={1600}
                  heightPx={600}
                  value={watchItems[index]?.desktopImageKey ?? ""}
                  onChange={(key) =>
                    setValue(`items.${index}.desktopImageKey`, key, {
                      shouldDirty: true,
                    })
                  }
                  disabled={submitting}
                  deferUpload
                  pendingFile={
                    pendingFiles[fieldKey(index, "desktopImageKey")] ?? null
                  }
                  onFileSelect={(file) =>
                    setPendingFile(index, "desktopImageKey", file)
                  }
                  onClear={() => clearPendingAndKey(index, "desktopImageKey")}
                />
              </div>
              <div className="flex items-center gap-4">
                <FormNumber
                  name={`items.${index}.order`}
                  label="ລຳດັບ"
                  min={0}
                />
                <FormCheckbox
                  name={`items.${index}.isActive`}
                  label="ເປີດໃຊ້ງານ"
                />
              </div>
            </div>
          ))}
        </div>
        <div className="flex gap-2 pt-2">
          <Button type="submit" disabled={submitting || uploading}>
            {uploading ? "ກຳລັງອັບໂຫຼດຮູບ..." : submitting ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel}>
            ຍົກເລີກ
          </Button>
        </div>
      </div>
    </FormRoot>
  );
}
